// components/admin/ProductForm.js - With Variants & Fixed Multiple Occasions
import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Upload, Image as ImageIcon, Trash2, Save, Plus, Minus,
  DollarSign, Tag, FileText, Globe, Calendar, Percent,
  Layers, Package, Flower2, Folder, Check, AlertCircle, TrendingDown,
  Palette, ShoppingBag, Heart
} from 'lucide-react';
import { toast } from 'react-toastify';
import { uploadImage, getProperImageUrl } from '../../config/appwrite';

const ProductForm = ({ product, category, subcategories = [], selectedSubcategory, onSubmit, onClose }) => {
  const fileInputRef = useRef(null);
  const variantImageInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: product?.name || '',
    price: product?.price || '',
    oldPrice: product?.oldPrice || '',
    description: product?.description || '',
    tag: product?.tag || '',
    origin: product?.origin || '',
    subcategory: product?.subcategory || selectedSubcategory?.name || '',
    subcategoryId: product?.subcategoryId || selectedSubcategory?.id || '',
    occasions: (product?.occasions && product.occasions.length > 0) ? product.occasions : (product?.occasion ? [product.occasion] : []),
    variants: product?.variants || []
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(() => {
    if (product?.image) {
      if (product.image.startsWith('http')) return product.image;
      const properUrl = getProperImageUrl(product.image);
      return properUrl || product.image;
    }
    return '';
  });
  
  // Variant states
  const [currentVariant, setCurrentVariant] = useState({
    color: '',
    size: '',
    price: '',
    images: []
  });
  const [currentVariantImages, setCurrentVariantImages] = useState([]);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate discount percentage
  const discountPercent = formData.oldPrice && formData.price && Number(formData.oldPrice) > Number(formData.price)
    ? Math.round(((formData.oldPrice - formData.price) / formData.oldPrice) * 100)
    : 0;

  const tagSuggestions = ['Best Seller', 'New Arrival', 'Premium', 'Limited Edition', 'Trending', 'Sale', 'Handpicked', 'Organic'];
  
  const colorSuggestions = ['Red', 'Pink', 'White', 'Yellow', 'Purple', 'Orange', 'Blue', 'Lavender', 'Peach', 'Cream', 'Mixed', 'Rainbow'];
  const sizeSuggestions = ['Small (6-8 stems)', 'Medium (12-15 stems)', 'Large (18-24 stems)', 'Premium (30+ stems)', 'Standard', 'Deluxe', 'Mini', 'Regular'];

  const occasionTypes = [
    { value: 'birthday', label: '🎂 Birthday' },
    { value: 'anniversary', label: '💑 Anniversary' },
    { value: 'wedding', label: '💒 Wedding' },
    { value: 'valentine', label: '💝 Valentine' },
    { value: 'mothersday', label: '🌸 Mother\'s Day' },
    { value: 'fathersday', label: '👔 Father\'s Day' },
    { value: 'diwali', label: '🪔 Diwali' },
    { value: 'christmas', label: '🎄 Christmas' },
    { value: 'engagement', label: '💍 Engagement' },
    { value: 'getwell', label: '🏥 Get Well Soon' },
    { value: 'justbecause', label: '💕 Just Because' }
  ];

  // Variant Functions
  const handleVariantImageUpload = async (files) => {
    const newImages = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        continue;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        newImages.push({ file, preview: reader.result });
        if (newImages.length === files.length) {
          setCurrentVariantImages(prev => [...prev, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeVariantImage = (index) => {
    setCurrentVariantImages(prev => prev.filter((_, i) => i !== index));
  };

  const addVariant = () => {
    if (!currentVariant.color && !currentVariant.size) {
      toast.error('Please enter at least color or size');
      return;
    }
    if (!currentVariant.price) {
      toast.error('Please enter price for this variant');
      return;
    }

    const newVariant = {
      id: Date.now().toString(),
      color: currentVariant.color || '',
      size: currentVariant.size || '',
      price: parseFloat(currentVariant.price),
      images: currentVariantImages.map(img => ({ preview: img.preview, file: img.file }))
    };

    let updatedVariants;
    if (editingVariantIndex !== null) {
      updatedVariants = [...formData.variants];
      updatedVariants[editingVariantIndex] = newVariant;
    } else {
      updatedVariants = [...formData.variants, newVariant];
    }

    setFormData(prev => ({ ...prev, variants: updatedVariants }));
    setCurrentVariant({ color: '', size: '', price: '', images: [] });
    setCurrentVariantImages([]);
    setShowVariantModal(false);
    setEditingVariantIndex(null);
    toast.success(editingVariantIndex !== null ? 'Variant updated!' : 'Variant added!');
  };

  const editVariant = (index) => {
    const variant = formData.variants[index];
    setCurrentVariant({
      color: variant.color || '',
      size: variant.size || '',
      price: variant.price || '',
      images: []
    });
    setCurrentVariantImages(variant.images?.map(img => ({ preview: img.preview || img.url, file: null })) || []);
    setEditingVariantIndex(index);
    setShowVariantModal(true);
  };

  const removeVariant = (index) => {
    if (window.confirm('Remove this variant?')) {
      setFormData(prev => ({
        ...prev,
        variants: prev.variants.filter((_, i) => i !== index)
      }));
    }
  };

  const toggleOccasion = (occasionValue) => {
    setFormData(prev => {
      const currentOccasions = [...prev.occasions];
      if (currentOccasions.includes(occasionValue)) {
        return { ...prev, occasions: currentOccasions.filter(o => o !== occasionValue) };
      } else {
        return { ...prev, occasions: [...currentOccasions, occasionValue] };
      }
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Product name required';
    if (!formData.price || formData.price <= 0) newErrors.price = 'Valid price required';
    if (formData.oldPrice && Number(formData.oldPrice) <= Number(formData.price)) {
      newErrors.oldPrice = 'Original price must be higher than selling price';
    }
    if (!product && !imageFile && !imagePreview) newErrors.image = 'Product image required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const processImageFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    if (errors.image) setErrors(prev => ({ ...prev, image: '' }));
  };

  // Upload variant images
  const uploadVariantImages = async (variant) => {
    const variantImages = [];
    for (const img of (variant.images || [])) {
      if (img.file) {
        const uploadResult = await uploadImage(img.file);
        variantImages.push({ url: uploadResult.url, id: uploadResult.id });
      } else if (img.url) {
        variantImages.push({ url: img.url, id: img.id });
      }
    }
    return { ...variant, images: variantImages };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix errors before submitting');
      return;
    }
    setLoading(true);
    try {
      let imageUrl = imagePreview;
      let imageId = null;
      
      if (imageFile) {
        const uploadResult = await uploadImage(imageFile);
        imageUrl = uploadResult.url;
        imageId = uploadResult.id;
      } else if (product?.imageId) {
        imageId = product.imageId;
      }
      
      // Upload variant images
      const variantsWithUrls = await Promise.all(formData.variants.map(uploadVariantImages));
      
      const submitData = {
        name: formData.name,
        price: parseFloat(formData.price),
        image: imageUrl,
        imageId: imageId,
        variants: variantsWithUrls,
        ...(formData.oldPrice && { oldPrice: parseFloat(formData.oldPrice) }),
        ...(formData.description && { description: formData.description }),
        ...(formData.tag && { tag: formData.tag }),
        ...(formData.origin && { origin: formData.origin }),
        ...(formData.subcategory && { subcategory: formData.subcategory }),
        ...(formData.subcategoryId && { subcategoryId: formData.subcategoryId }),
        // Store occasions as an array but product will be created once
        occasions: formData.occasions
      };
      
      await onSubmit(submitData);
    } catch (error) {
      toast.error('Failed to save: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        backdropFilter: 'blur(2px)'
      }} />
      
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: 'white', borderRadius: '20px', zIndex: 1001, width: '95%', maxWidth: '700px',
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: 'white',
          zIndex: 2
        }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '32px', height: '32px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: '16px'
              }}>{product ? '✏️' : '+'}</span>
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
            <p style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
              Category: {category?.name || category || 'Select Category'}
            </p>
          </div>
          <button onClick={onClose} style={{
            width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9',
            border: 'none', cursor: 'pointer', fontSize: '18px'
          }}>✕</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {/* Subcategory Selection */}
          {subcategories && subcategories.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}><Folder size={14} /> Subcategory</label>
              <select 
                value={formData.subcategoryId || formData.subcategory}
                onChange={(e) => {
                  const selected = subcategories.find(s => s.id === e.target.value || s.name === e.target.value);
                  if (selected) {
                    setFormData(prev => ({ 
                      ...prev, 
                      subcategoryId: selected.id, 
                      subcategory: selected.name 
                    }));
                  }
                }}
                style={selectStyle}
              >
                <option value="">Select subcategory</option>
                {subcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Product Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}><Package size={14} /> Product Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Red Rose Bouquet"
              style={inputStyle(errors.name)}
            />
            {errors.name && <p style={errorStyle}>{errors.name}</p>}
          </div>

          {/* Price Row with Discount Display */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelStyle}><DollarSign size={14} /> Selling Price (₹) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="999"
                  style={inputStyle(errors.price)}
                />
                {errors.price && <p style={errorStyle}>{errors.price}</p>}
              </div>
              <div>
                <label style={labelStyle}><Percent size={14} /> Original Price (₹)</label>
                <input
                  type="number"
                  name="oldPrice"
                  value={formData.oldPrice}
                  onChange={handleChange}
                  placeholder="1499"
                  style={inputStyle(errors.oldPrice)}
                />
                {errors.oldPrice && <p style={errorStyle}>{errors.oldPrice}</p>}
              </div>
            </div>

            {/* Discount Card */}
            {discountPercent > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #fef2f2, #fff5f5)',
                borderRadius: '12px',
                padding: '12px 16px',
                border: '1px solid #dc2626',
                marginTop: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{
                    background: '#dc2626',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '30px',
                    fontSize: '14px',
                    fontWeight: 700
                  }}>
                    {discountPercent}% OFF
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Customer saves </span>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#dc2626' }}>
                      ₹{(formData.oldPrice - formData.price).toLocaleString()}
                    </span>
                  </div>
                  <TrendingDown size={16} color="#10b981" style={{ marginLeft: 'auto' }} />
                </div>
              </div>
            )}
          </div>

          {/* Variants Section */}
          <div style={{ marginBottom: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <label style={labelStyle}><Layers size={14} /> Product Variants</label>
              <button
                type="button"
                onClick={() => { setEditingVariantIndex(null); setCurrentVariant({ color: '', size: '', price: '', images: [] }); setCurrentVariantImages([]); setShowVariantModal(true); }}
                style={{
                  padding: '6px 12px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={12} /> Add Variant
              </button>
            </div>
            
            {formData.variants.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1' }}>
                <Palette size={28} color="#94a3b8" />
                <p style={{ color: '#64748b', fontSize: '12px', marginTop: '8px' }}>No variants added yet</p>
                <p style={{ color: '#94a3b8', fontSize: '11px' }}>Add colors, sizes, or styles with different prices</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {formData.variants.map((variant, idx) => (
                  <div key={idx} style={{ background: '#f8fafc', borderRadius: '10px', padding: '10px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        {variant.color && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginRight: '12px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: variant.color.toLowerCase() }} />
                            <strong>{variant.color}</strong>
                          </span>
                        )}
                        {variant.size && <span style={{ marginRight: '12px' }}><strong>Size:</strong> {variant.size}</span>}
                        <span><strong>Price:</strong> <span style={{ color: '#dc2626' }}>₹{variant.price}</span></span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button type="button" onClick={() => editVariant(idx)} style={{ padding: '4px 10px', background: '#e3f2fd', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Edit</button>
                        <button type="button" onClick={() => removeVariant(idx)} style={{ padding: '4px 10px', background: '#fef2f2', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', color: '#dc2626' }}>Remove</button>
                      </div>
                    </div>
                    {variant.images && variant.images.length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                        {variant.images.slice(0, 3).map((img, imgIdx) => (
                          <img key={imgIdx} src={img.preview || img.url} alt="" style={{ width: '30px', height: '30px', borderRadius: '4px', objectFit: 'cover' }} />
                        ))}
                        {variant.images.length > 3 && <span style={{ fontSize: '10px', color: '#64748b' }}>+{variant.images.length - 3}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}><FileText size={14} /> Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Describe your product..."
              style={{ ...inputStyle(), resize: 'vertical' }}
            />
          </div>

          {/* Tag */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}><Tag size={14} /> Product Tag</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {tagSuggestions.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tag: prev.tag === tag ? '' : tag }))}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    border: `1px solid ${formData.tag === tag ? '#dc2626' : '#e2e8f0'}`,
                    background: formData.tag === tag ? '#fff5f5' : 'white',
                    color: formData.tag === tag ? '#dc2626' : '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
            <input
              type="text"
              name="tag"
              value={formData.tag}
              onChange={handleChange}
              placeholder="Or custom tag"
              style={inputStyle()}
            />
          </div>

          {/* Origin */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}><Globe size={14} /> Origin (Optional)</label>
            <input
              type="text"
              name="origin"
              value={formData.origin}
              onChange={handleChange}
              placeholder="e.g., Netherlands, Local"
              style={inputStyle()}
            />
          </div>

          {/* Multiple Occasions */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}><Calendar size={14} /> Suitable Occasions</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
              {occasionTypes.map(occ => (
                <button
                  key={occ.value}
                  type="button"
                  onClick={() => toggleOccasion(occ.value)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    border: `1.5px solid ${formData.occasions.includes(occ.value) ? '#dc2626' : '#e2e8f0'}`,
                    background: formData.occasions.includes(occ.value) ? '#fff5f5' : 'white',
                    color: formData.occasions.includes(occ.value) ? '#dc2626' : '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  {occ.label}
                  {formData.occasions.includes(occ.value) && <Check size={10} style={{ marginLeft: '4px', display: 'inline' }} />}
                </button>
              ))}
            </div>
            {formData.occasions.length > 0 && (
              <p style={{ fontSize: '10px', color: '#10b981' }}>
                ✓ Selected for {formData.occasions.length} occasion(s) - Product will appear in all selected categories
              </p>
            )}
          </div>

          {/* Image Upload */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}><ImageIcon size={14} /> Product Image *</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) processImageFile(e.dataTransfer.files[0]); }}
              style={{
                border: `2px dashed ${dragActive ? '#dc2626' : errors.image ? '#fca5a5' : '#cbd5e1'}`,
                borderRadius: '12px',
                padding: imagePreview ? '16px' : '30px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragActive ? '#fff5f5' : '#fafbfc'
              }}
            >
              <input ref={fileInputRef} type="file" accept="image/*" onChange={e => e.target.files?.[0] && processImageFile(e.target.files[0])} style={{ display: 'none' }} />
              {imagePreview ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }} />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setImageFile(null); setImagePreview(''); }}
                    style={{
                      position: 'absolute', top: '-8px', right: '-8px',
                      width: '24px', height: '24px', borderRadius: '50%',
                      background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={28} color="#dc2626" style={{ marginBottom: '8px' }} />
                  <p style={{ fontSize: '13px', color: '#334155' }}>Click or drag image</p>
                  <p style={{ fontSize: '10px', color: '#94a3b8' }}>JPG, PNG up to 5MB</p>
                </>
              )}
            </div>
            {errors.image && <p style={errorStyle}>{errors.image}</p>}
          </div>

          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button type="button" onClick={onClose} style={{
              flex: 1, padding: '10px', background: '#f1f5f9', border: 'none',
              borderRadius: '10px', fontWeight: 600, cursor: 'pointer'
            }}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={{
              flex: 2, padding: '10px', background: loading ? '#94a3b8' : '#dc2626',
              color: 'white', border: 'none', borderRadius: '10px',
              fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}>
              {loading ? <div className="spinner" style={{ width: '16px', height: '16px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> : <Save size={16} />}
              {loading ? 'Saving...' : (product ? 'Update Product' : 'Add Product')}
            </button>
          </div>
        </form>
      </div>

      {/* Variant Modal */}
      {showVariantModal && (
        <>
          <div onClick={() => setShowVariantModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100 }} />
          <div style={{ 
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
            background: 'white', borderRadius: '20px', zIndex: 1101, width: '90%', maxWidth: '500px', 
            maxHeight: '85vh', overflow: 'auto', padding: '24px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a0a0a' }}>
                {editingVariantIndex !== null ? 'Edit Variant' : 'Add New Variant'}
              </h3>
              <button onClick={() => setShowVariantModal(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
            </div>
            
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
              Add different colors, sizes, or styles with their own prices and images.
            </p>

            {/* Color Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Color / Flower Type</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                {colorSuggestions.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setCurrentVariant({ ...currentVariant, color })}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      border: `1.5px solid ${currentVariant.color === color ? '#dc2626' : '#e2e8f0'}`,
                      background: currentVariant.color === color ? '#fff5f5' : 'white',
                      color: currentVariant.color === color ? '#dc2626' : '#64748b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: color.toLowerCase() }} />
                    {color}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={currentVariant.color}
                onChange={e => setCurrentVariant({ ...currentVariant, color: e.target.value })}
                placeholder="Or type custom color"
                style={inputStyle()}
              />
            </div>

            {/* Size Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Size / Arrangement</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                {sizeSuggestions.slice(0, 6).map(size => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setCurrentVariant({ ...currentVariant, size })}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      border: `1.5px solid ${currentVariant.size === size ? '#dc2626' : '#e2e8f0'}`,
                      background: currentVariant.size === size ? '#fff5f5' : 'white',
                      color: currentVariant.size === size ? '#dc2626' : '#64748b',
                      cursor: 'pointer'
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={currentVariant.size}
                onChange={e => setCurrentVariant({ ...currentVariant, size: e.target.value })}
                placeholder="Or type custom size"
                style={inputStyle()}
              />
            </div>

            {/* Price */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Price for this variant (₹) *</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>₹</span>
                <input
                  type="number"
                  value={currentVariant.price}
                  onChange={e => setCurrentVariant({ ...currentVariant, price: e.target.value })}
                  placeholder="e.g., 1499"
                  style={{ ...inputStyle(), paddingLeft: '32px' }}
                />
              </div>
            </div>

            {/* Variant Images */}
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Variant Images</label>
              <div style={{ 
                border: '2px dashed #cbd5e1', 
                borderRadius: '12px', 
                padding: '16px', 
                textAlign: 'center',
                background: '#fafbfc'
              }}>
                <button
                  type="button"
                  onClick={() => variantImageInputRef.current?.click()}
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto' }}
                >
                  <Upload size={16} /> Upload Images
                </button>
                <input ref={variantImageInputRef} type="file" multiple accept="image/*" onChange={e => e.target.files && handleVariantImageUpload(Array.from(e.target.files))} style={{ display: 'none' }} />
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '16px', justifyContent: 'center' }}>
                  {currentVariantImages.map((img, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={img.preview} alt="" style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                      <button
                        type="button"
                        onClick={() => removeVariantImage(idx)}
                        style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%', background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer', fontSize: '10px' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowVariantModal(false)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
                Cancel
              </button>
              <button onClick={addVariant} style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                {editingVariantIndex !== null ? 'Update Variant' : 'Add Variant'}
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#334155',
  marginBottom: '6px'
};

const inputStyle = (hasError) => ({
  width: '100%',
  padding: '10px 12px',
  border: hasError ? '1.5px solid #fca5a5' : '1.5px solid #e2e8f0',
  borderRadius: '10px',
  fontSize: '13px',
  outline: 'none',
  fontFamily: 'inherit'
});

const selectStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1.5px solid #e2e8f0',
  borderRadius: '10px',
  fontSize: '13px',
  outline: 'none',
  background: 'white'
};

const errorStyle = {
  color: '#dc2626',
  fontSize: '10px',
  marginTop: '4px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

export default ProductForm;